import { useState, useCallback, useMemo } from "react";
import { Post } from "@/core/types/postTypes";
import { useAppStore } from "@/core/store/useAppStore";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { usePostCardAuthOptional } from "../contexts/PostCardAuthContext";
import { UserRole } from "@/services/urlService";

/**
 * Shared hook for converting a Post's media items with auth context,
 * carousel cycling, reel detection, and video player state.
 *
 * Used by ListPostCard (which doesn't wrap PostCard) and potentially
 * other components that need post media logic.
 */
export function usePostMedia(post: Post) {
  const { selectedClient } = useAppStore();
  const authContext = usePostCardAuthOptional();

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isVideoPlayerOpen, setIsVideoPlayerOpen] = useState(false);
  const [selectedVideoForPlayer, setSelectedVideoForPlayer] = useState<MediaItem | null>(null);

  // Convert plain objects to MediaItem instances using auth context, then sort
  const sortedMediaItems = useMemo(() => {
    if (!post?.mediaItems || post.mediaItems.length === 0) return [];

    let userRole = authContext?.userRole || UserRole.ADMIN;
    let clientId = authContext?.clientId || selectedClient?.id;
    let authToken = authContext?.authToken;

    if (!authContext && userRole === UserRole.ADMIN) {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          authToken = user.accessToken || undefined;
        } catch {
          authToken = undefined;
        }
      }
    }

    if (!clientId) return [];
    if (userRole === UserRole.CLIENT && (!authToken || authToken.trim() === '')) return [];

    const instances = post.mediaItems.map((item) => {
      const backendData = item instanceof MediaItem ? item.backendData : item;
      return MediaItem.fromBackend(backendData, userRole, clientId!, authToken);
    });

    return instances.sort((a, b) => (a.position ?? 999) - (b.position ?? 999));
  }, [post?.mediaItems, authContext, selectedClient?.id]);

  const imageCount = sortedMediaItems?.length || 0;

  const getReelVideoItem = useCallback(() => {
    if (post.postType !== "reel") return null;
    return sortedMediaItems.find((item) => item.isVideo()) || null;
  }, [post.postType, sortedMediaItems]);

  const getCurrentImage = useCallback(() => {
    if (post.postType === "reel") {
      if (post.thumbnailMediaItem) return post.thumbnailMediaItem;
      if (post.thumbnailMediaId && sortedMediaItems) {
        const thumbnailId = typeof post.thumbnailMediaId === "string"
          ? parseInt(post.thumbnailMediaId)
          : post.thumbnailMediaId;
        const thumbnailItem = sortedMediaItems.find((item) => item.id === thumbnailId);
        if (thumbnailItem && thumbnailItem.isImage()) return thumbnailItem;
      }
      if (sortedMediaItems) {
        const imageItem = sortedMediaItems.find((item) => item.isImage());
        if (imageItem) return imageItem;
      }
      return getReelVideoItem();
    }

    if (!sortedMediaItems || sortedMediaItems.length === 0) return null;
    if (post.isCarousel && sortedMediaItems.length > 1) {
      const imageIndex = Math.min(currentImageIndex, sortedMediaItems.length - 1);
      return sortedMediaItems[imageIndex];
    }
    return sortedMediaItems[0];
  }, [sortedMediaItems, post.isCarousel, post.postType, post.thumbnailMediaItem, currentImageIndex, getReelVideoItem]);

  const handleImageClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (post.isCarousel && sortedMediaItems && sortedMediaItems.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % sortedMediaItems.length);
    }
  }, [post.isCarousel, sortedMediaItems]);

  const handleVideoClick = useCallback((video: MediaItem) => {
    setSelectedVideoForPlayer(video);
    setIsVideoPlayerOpen(true);
  }, []);

  const handleVideoPlayerClose = useCallback(() => {
    setIsVideoPlayerOpen(false);
    setSelectedVideoForPlayer(null);
  }, []);

  const getStreamUrlByFileId = useCallback((fileId: string): string | undefined => {
    const currentClientId = authContext?.clientId || selectedClient?.id;
    if (!currentClientId) return undefined;
    const mediaItem = sortedMediaItems.find(item =>
      item.fileIdentity === fileId
    );
    if (!mediaItem) return undefined;
    return mediaItem.getStreamUrlWithQueryAuth();
  }, [authContext?.clientId, selectedClient?.id, sortedMediaItems]);

  return {
    sortedMediaItems,
    imageCount,
    currentImageIndex,
    currentImage: getCurrentImage(),
    getReelVideoItem,
    handleImageClick,
    handleVideoClick,
    handleVideoPlayerClose,
    isVideoPlayerOpen,
    selectedVideoForPlayer,
    getStreamUrlByFileId,
    authContext,
    selectedClient,
  };
}
